import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Loader2, Sparkles, ShieldAlert } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import MultiSelectChips from "../components/MultiSelectChips";

const EMPTY = [];

export default function Join() {
  const { token } = useParams();
  const nav = useNavigate();
  const { register } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState({ countries: [], industries: [], functions: [] });
  const [form, setForm] = useState({
    name: "", company: "", email: "", password: "",
    biography: "", expertise: [], countries: [], industries: [], functions: [],
    whatsapp: "", booking_link: "", profile_photo: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/invites/preview", { params: { token } })
      .then((r) => setPreview(r.data))
      .catch(() => setPreview({ valid: false, error: "Invite could not be checked" }))
      .finally(() => setLoading(false));
    api.get("/catalog").then((r) => setCatalog(r.data));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.countries.length === 0 || form.industries.length === 0 || form.functions.length === 0) {
      toast.error("Select at least one country, industry, and function");
      return;
    }
    setBusy(true);
    try {
      const u = await register({ ...form, invite_token: token });
      toast.success(`Welcome — your Growth Campaign™ link is bizpulseai.net/c/${u.slug}`, { duration: 8000 });
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 mx-auto animate-spin" /><div className="mt-3">Verifying invite…</div></div>;

  if (!preview?.valid) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20">
        <Card className="card-elevated p-10 text-center">
          <ShieldAlert className="w-10 h-10 text-amber-400 mx-auto" />
          <h1 className="font-display text-3xl mt-5">Invite unavailable</h1>
          <p className="text-muted-foreground mt-3">{preview?.error || "This invite link is no longer valid."}</p>
          <Link to="/" className="inline-block mt-6"><Button variant="outline">Back to home</Button></Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      <Card className="card-elevated p-5 flex items-center gap-4 mb-8" data-testid="join-banner">
        <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center"><Sparkles className="w-5 h-5 text-accent-foreground" /></div>
        <div>
          <div className="font-sans-ui font-semibold">Free beta access activated</div>
          <div className="text-sm text-muted-foreground">
            Plan: <span className="text-foreground capitalize">{preview.plan}</span>
            {preview.note ? <> · {preview.note}</> : null}
          </div>
        </div>
      </Card>

      <div className="overline"><span className="accent-line" /> One-step signup</div>
      <h1 className="font-display text-4xl mt-3">Create your BizPulse profile</h1>
      <p className="text-muted-foreground mt-2">Your plan and access have been pre-granted by this invite link.</p>

      <form className="mt-10 space-y-7" onSubmit={submit}>
        <Card className="card-elevated p-7 grid md:grid-cols-2 gap-5">
          <Field label="Full name" required><Input data-testid="join-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required /></Field>
          <Field label="Company"><Input data-testid="join-company" value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} /></Field>
          <Field label="Email" required><Input data-testid="join-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required /></Field>
          <Field label="Password" required><Input data-testid="join-password" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required minLength={6} /></Field>
          <Field label="WhatsApp (with country code)"><Input data-testid="join-whatsapp" value={form.whatsapp} onChange={(e) => setForm({...form, whatsapp: e.target.value})} placeholder="+966 55 555 5555" /></Field>
          <Field label="Booking link"><Input data-testid="join-booking" value={form.booking_link} onChange={(e) => setForm({...form, booking_link: e.target.value})} placeholder="https://cal.com/yourname" /></Field>
        </Card>

        <Card className="card-elevated p-7 space-y-5">
          <Field label="Biography"><Textarea data-testid="join-bio" rows={3} value={form.biography} onChange={(e) => setForm({...form, biography: e.target.value})} /></Field>
          <Field label="Areas of expertise"><MultiSelectChips testid="join-expertise" value={form.expertise} onChange={(v) => setForm({...form, expertise: v})} options={EMPTY} allowFree /></Field>
          <Field label="Countries served"><MultiSelectChips testid="join-countries" value={form.countries} onChange={(v) => setForm({...form, countries: v})} options={catalog.countries} /></Field>
          <Field label="Industries served"><MultiSelectChips testid="join-industries" value={form.industries} onChange={(v) => setForm({...form, industries: v})} options={catalog.industries} /></Field>
          <Field label="Business functions"><MultiSelectChips testid="join-functions" value={form.functions} onChange={(v) => setForm({...form, functions: v})} options={catalog.functions} /></Field>
        </Card>

        <div className="flex justify-end">
          <Button data-testid="join-submit" disabled={busy} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-7">
            {busy ? "Creating…" : "Activate my account"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}{required && <span className="text-accent"> *</span>}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
